import valueEqual from 'value-equal';



const formats = {
  cardExpiration: {
    segments: [2, 2],
    separator: '/',
    maxLeadingDigits: [1]
  },

  cvv: {
    segments: [4],
    separator: ''
  },

  dateAndMonth: {
    segments: [2, 2],
    separator: '/',
    maxLeadingDigits: [3, 1]
  },

  dateMonthAndYear: {
    segments: [2, 2, 4],
    separator: '/',
    maxLeadingDigits: [3, 1]
  },

  giftCard: {
    allowAlpha: true,
    segments: [4, 4, 4, 4, 4, 4, 4, 4, 4, 4],
    separator: ' '
  },

  monthAndDate: {
    segments: [2, 2],
    separator: '/',
    maxLeadingDigits: [1, 3]
  },

  monthDateAndYear: {
    segments: [2, 2, 4],
    separator: '/',
    maxLeadingDigits: [1, 3]
  },

  phone: {
    template: '(XXX) XXX-XXXX'
  },

  postalCode: {
    allowAlpha: true,
    autoCapitalize: true
    /* segments and separator get set depending on the first character entered */
  }
};



/*
  Per-country postal-code mask templates, salvaged from the 2029 branch.
  Used when the directive is invoked with `{ type: 'postalCode', region: 'XX' }`.
  The legacy string form `'postalCode'` keeps the original US/CA auto-detect
  behavior for callers that haven't been migrated to country-aware masks.
*/
const POSTAL_FORMAT_TEMPLATES = {
  DIGITS_3: { segments: [3], separator: '', allowAlpha: false },
  DIGITS_4: { segments: [4], separator: '', allowAlpha: false },
  DIGITS_5: { segments: [5], separator: '', allowAlpha: false },
  DIGITS_6: { segments: [6], separator: '', allowAlpha: false },
  CANADIAN: { segments: [3, 3], separator: ' ', allowAlpha: true, autoCapitalize: true },
  JAPANESE: { segments: [3, 4], separator: '-', allowAlpha: false },
  BRAZILIAN: { segments: [5, 3], separator: '-', allowAlpha: false },
  UK_FLEXIBLE: { segments: [8], separator: '', allowAlpha: true, autoCapitalize: true }
};

const POSTAL_CODE_FORMATS = {
  US: 'DIGITS_5',
  CA: 'CANADIAN',
  AU: 'DIGITS_4',
  NZ: 'DIGITS_4',
  GB: 'UK_FLEXIBLE',
  IE: 'UK_FLEXIBLE',
  JP: 'JAPANESE',
  IN: 'DIGITS_6',
  CN: 'DIGITS_6',
  DE: 'DIGITS_5',
  FR: 'DIGITS_5',
  IT: 'DIGITS_5',
  ES: 'DIGITS_5',
  MX: 'DIGITS_5',
  BR: 'BRAZILIAN',
  IS: 'DIGITS_3'
};

function getPostalCodeFormat(regionCode) {
  const templateName = POSTAL_CODE_FORMATS[regionCode];
  if (templateName && POSTAL_FORMAT_TEMPLATES[templateName]) {
    return { ...POSTAL_FORMAT_TEMPLATES[templateName] };
  }
  return null;
}



// A segment gets a leading "0" when its first digit can't start a two-digit value, or when the user closes it after one digit by typing the separator.
function padSegments(value, format) {
  const parts = value.split(format.separator);
  let digits = parts
    .map((part, i) => {
      const partDigits = part.replace(/[^0-9]/g, '');
      const isClosed = i < parts.length - 1;
      return isClosed && /^[1-9]$/.test(partDigits) && format.maxLeadingDigits[i] != null ? `0${partDigits}` : partDigits;
    })
    .join('');
  let position = 0;

  format.maxLeadingDigits.forEach((maxLeadingDigit, i) => {
    if (Number(digits[position]) > maxLeadingDigit) {
      digits = `${digits.slice(0, position)}0${digits.slice(position)}`;
    }

    position += format.segments[i];
  });

  return digits;
}



function padOnBlur(event) {
  const element = event.target;
  const format = formats[element.dataset.maskType];

  if (!format || !format.maxLeadingDigits) return;

  const parts = element.value.split(format.separator);
  const lastIndex = parts.length - 1;

  if (/^[1-9]$/.test(parts[lastIndex]) && format.maxLeadingDigits[lastIndex] != null) {
    parts[lastIndex] = `0${parts[lastIndex]}`;
    updateValue(element, parts.join(format.separator));
  }
}



function mask(element, type) {
  const { value } = element;
  const { oldValue } = element.dataset;
  let strippedValue = element.value.replace(/[^0-9]/g, '');
  let strippedOldValue = oldValue.replace(/[^0-9]/g, '');
  let maskedValue = '';
  let position = 0;
  let format;


  /*
    Country-aware form: `{ type: 'postalCode', region: 'GB' }`. We resolve
    to a predefined template and skip the auto-detect branch below. If the
    region isn't in our map, fall through to the auto-detect path so we at
    least handle US/CA-shaped postcodes.
  */
  if (typeof type === 'object' && type !== null && type.type === 'postalCode' && type.region) {
    const predefined = getPostalCodeFormat(type.region);
    if (predefined) {
      format = { ...formats.postalCode, ...predefined };
    }
    else {
      format = { ...formats.postalCode };
    }
  }
  else if (typeof type === 'string') {
    format = formats[type];
  }
  else {
    format = type;
  }

  if (format.allowAlpha) {
    strippedValue = element.value.replace(/[^0-9A-Za-z]/g, '');
    strippedOldValue = oldValue.replace(/[^0-9A-Za-z]/g, '');
  }

  /*
    Legacy auto-detect — only fires for the string `'postalCode'` form (US/CA).
    The country-aware object form has already populated `format` above and
    skips this branch.
  */
  if (type === 'postalCode') {
    /* value starts with letter */
    if (/^[a-z]/i.test(strippedValue)) {
      /* Canadian Postal Code Format */
      format.allowAlpha = true;
      format.segments = [3, 3];
      format.separator = ' ';
    }
    else if (
      /^[0-9]/.test(strippedValue) /* value starts with number */
      && /[^a-z]/ig.test(strippedValue) /* and has no letters */
    ) {
      /* US ZIP Code Format */
      format.allowAlpha = false;
      format.segments = [5];
      format.separator = '';
    }
    /* there is no value */
    else {
      format.allowAlpha = true;
    }
  }

  const isDeleting = strippedValue < strippedOldValue || (strippedValue === strippedOldValue && /[^0-9A-Za-z]/.test(oldValue[oldValue.length - 1]));

  if (isDeleting || !element.value) {
    updateValue(element, element.value);
    return;
  }

  // Prepend a "0" to the front of certain inputs (turns 3/12 in to 03/12 automatically):

  if (format.maxLeadingDigits) {
    strippedValue = padSegments(value, format);
  }

  // Strip country codes out of phone numbers:

  if (type === 'phone' && strippedValue.length > 10 && strippedValue[0] === '1') {
    strippedValue = strippedValue.substr(-10);
  }

  if (format.template) {
    const strippedValues = [...strippedValue];

    [...format.template].every((template) => {
      if (template === 'X') {
        const character = strippedValues.shift();

        if (character) {
          maskedValue += character;
          return true;
        }

        return false;
      }

      maskedValue += template;
      return true;
    });
  }

  else {
    format.segments.forEach((segment, i) => {
      let characters = strippedValue.substr(position, segment);
      const isLastSegment = (i + 1) === format.segments.length;

      if (characters.length === segment && !isLastSegment) {
        characters += format.separator;
      }

      maskedValue += characters;
      position += segment;
    });
  }

  if (format.autoCapitalize) {
    maskedValue = maskedValue.toUpperCase();
  }

  updateValue(element, maskedValue);
}



function updateValue(element, maskedValue) {
  let caretPosition = document.activeElement.selectionEnd;
  const caretIsAtEnd = element.value.length === caretPosition;
  const inputEvent = new Event('input', {
    bubbles: true
  });

  element.value = maskedValue;
  element.dataset.oldValue = maskedValue;

  // if cursor was at end when user changed input, push it to end, else keep it where it was:

  if (document.activeElement === element) {
    if (caretIsAtEnd) {
      caretPosition = element.value.length;
    }

    element.setSelectionRange(caretPosition, caretPosition);
  }

  element.dispatchEvent(inputEvent);
}



function applyMask(element, binding) {
  element.dataset.oldValue = element.dataset.oldValue || '';
  const valueChanged = element.dataset.oldValue !== element.value;
  const type = binding.value;
  const typeChanged = !valueEqual(type, binding.oldValue);

  element.dataset.maskType = typeof type === 'string' ? type : '';

  if ((valueChanged || typeChanged) && type) {
    mask(element, type);
  }
}



const maskInput = {
  // Skip the mount call when the input is empty — there's nothing to format,
  // and the postalCode branch mutates the shared `formats.postalCode` object
  // when called with an empty value, which would cross-contaminate later renders.
  mounted(element, binding) {
    element.addEventListener('blur', padOnBlur);

    if (element.value) {
      applyMask(element, binding);
    }
  },
  updated: applyMask,
  beforeUnmount(element) {
    element.removeEventListener('blur', padOnBlur);
  }
};



export default maskInput;
